import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequest, msalInstance } from '../auth/msalInstance';
import { appConfig } from '../config';
import type { ProblemDetails } from './types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly problem?: ProblemDetails,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get validationMessages(): string[] {
    return Object.values(this.problem?.errors ?? {}).flat();
  }
}

async function acquireAccessToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new ApiError(401, 'You are not signed in.');
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiRequest, account });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // The refresh token expired or consent is missing: only a full redirect can fix it.
      await msalInstance.acquireTokenRedirect({ ...apiRequest, account });
      throw new ApiError(401, 'Your session expired, redirecting to sign in…');
    }

    throw error;
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  let problem: ProblemDetails | undefined;
  try {
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('json')) {
      problem = (await response.json()) as ProblemDetails;
    }
  } catch {
    // A non-JSON error body is not worth failing over; the status code carries the meaning.
  }

  const validation = Object.values(problem?.errors ?? {}).flat();
  const message =
    validation[0] ?? problem?.detail ?? problem?.title ?? `Request failed with status ${response.status}.`;

  return new ApiError(response.status, message, problem);
}

async function request(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await acquireAccessToken();
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init.body !== undefined && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${appConfig.apiBaseUrl}${path}`, { ...init, headers });
  if (!response.ok) {
    throw await toApiError(response);
  }

  return response;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await request(path, init);
  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function apiSend(path: string, method: string, body?: unknown): Promise<void> {
  await request(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export async function apiSendJson<T>(path: string, method: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export async function apiFetchBlob(path: string): Promise<Blob> {
  const response = await request(path);
  return response.blob();
}

/**
 * Sends a JSON body and reads a binary payload back. A contact card is rendered from the fields themselves
 * rather than from a stored code, so its image comes from a POST - which `apiFetchBlob` cannot do.
 */
export async function apiSendForBlob(path: string, method: string, body: unknown): Promise<Blob> {
  const response = await request(path, { method, body: JSON.stringify(body) });
  return response.blob();
}

export async function apiSendForm<T>(path: string, form: FormData): Promise<T> {
  return apiFetch<T>(path, { method: 'POST', body: form });
}
